// R-167 WU-6a — CustomCounter editor modal (create + edit).
//
// Used by `/admin/counters`. In create mode the operator picks scope
// (tenant vs job-type) and name. In edit mode name + scope are locked
// (they participate in the unique index `uniq_counter_scope_name`) —
// admins can only tweak min/max/padding/cadence.
//
// Validation gates the Save button:
//   - name must match `^[a-zA-Z][a-zA-Z0-9_]*$` (matches WU-3 token grammar)
//   - max >= min
//   - 0 <= padding <= 10
import { useMemo, useState } from "react";
import type { CustomCounter, ResetCadence } from "../api/client";
import styles from "../counters.module.css";

export interface JobTypeOption {
  id: string;
  name: string;
}

export interface CounterEditorSubmitPayload {
  name: string;
  jobTypeId: string | null;
  minValue: number;
  maxValue: number;
  paddingWidth: number;
  resetCadence: ResetCadence;
}

export interface CounterEditorModalProps {
  mode: "create" | "edit";
  initial?: Partial<CustomCounter>;
  jobTypes: JobTypeOption[];
  onClose: () => void;
  onSubmit: (payload: CounterEditorSubmitPayload) => void;
}

const NAME_RE = /^[a-zA-Z][a-zA-Z0-9_]*$/;

export default function CounterEditorModal({
  mode,
  initial,
  jobTypes,
  onClose,
  onSubmit,
}: CounterEditorModalProps) {
  const [name, setName] = useState(initial?.name ?? "");
  const [jobTypeId, setJobTypeId] = useState<string | null>(
    initial?.jobTypeId ?? null,
  );
  const [minValue, setMinValue] = useState<string>(
    String(initial?.minValue ?? 1),
  );
  const [maxValue, setMaxValue] = useState<string>(
    String(initial?.maxValue ?? 9999),
  );
  const [paddingWidth, setPaddingWidth] = useState<string>(
    String(initial?.paddingWidth ?? 0),
  );
  const [resetCadence, setResetCadence] = useState<ResetCadence>(
    initial?.resetCadence ?? "never",
  );

  const nameValid = NAME_RE.test(name);
  const minN = Number.parseInt(minValue, 10);
  const maxN = Number.parseInt(maxValue, 10);
  const padN = Number.parseInt(paddingWidth, 10);
  const rangeValid =
    Number.isFinite(minN) && Number.isFinite(maxN) && maxN >= minN;
  const padValid = Number.isFinite(padN) && padN >= 0 && padN <= 10;

  const canSave = useMemo(
    () => nameValid && rangeValid && padValid,
    [nameValid, rangeValid, padValid],
  );

  function handleSubmit() {
    onSubmit({
      name,
      jobTypeId,
      minValue: minN,
      maxValue: maxN,
      paddingWidth: padN,
      resetCadence,
    });
  }

  return (
    <div className={styles.modalBackdrop} role="dialog" aria-modal="true">
      <div className={styles.modal}>
        <h2>{mode === "create" ? "Create counter" : "Edit counter"}</h2>

        <div className={styles.field}>
          <label htmlFor="counter-name">
            <span>Name</span>
          </label>
          <input
            id="counter-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={mode === "edit"}
            placeholder="batchSeq"
            aria-label="name"
          />
          <small className={styles.help}>
            Alphanumeric + underscore. Leading letter. Reference as{" "}
            <code>{`{counter:${name || "name"}}`}</code> in templates.
          </small>
        </div>

        <div className={styles.field}>
          <label htmlFor="counter-scope">
            <span>Scope</span>
          </label>
          <select
            id="counter-scope"
            value={jobTypeId ?? "__tenant__"}
            onChange={(e) =>
              setJobTypeId(
                e.target.value === "__tenant__" ? null : e.target.value,
              )
            }
            disabled={mode === "edit"}
            aria-label="scope"
          >
            <option value="__tenant__">
              Tenant (shared across all job types)
            </option>
            {jobTypes.map((jt) => (
              <option key={jt.id} value={jt.id}>
                Job Type: {jt.name}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.fieldRow}>
          <div className={styles.field}>
            <label htmlFor="counter-min">
              <span>Min</span>
            </label>
            <input
              id="counter-min"
              type="number"
              value={minValue}
              onChange={(e) => setMinValue(e.target.value)}
              aria-label="min"
            />
          </div>
          <div className={styles.field}>
            <label htmlFor="counter-max">
              <span>Max</span>
            </label>
            <input
              id="counter-max"
              type="number"
              value={maxValue}
              onChange={(e) => setMaxValue(e.target.value)}
              aria-label="max"
            />
          </div>
          <div className={styles.field}>
            <label htmlFor="counter-padding">
              <span>Padding</span>
            </label>
            <input
              id="counter-padding"
              type="number"
              min={0}
              max={10}
              value={paddingWidth}
              onChange={(e) => setPaddingWidth(e.target.value)}
              aria-label="padding"
            />
          </div>
        </div>

        <div className={styles.field}>
          <label htmlFor="counter-cadence">
            <span>Reset cadence</span>
          </label>
          <select
            id="counter-cadence"
            value={resetCadence}
            onChange={(e) => setResetCadence(e.target.value as ResetCadence)}
            aria-label="reset cadence"
          >
            <option value="never">Never</option>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly (ISO)</option>
            <option value="monthly">Monthly</option>
            <option value="yearly">Yearly</option>
          </select>
          <small className={styles.help}>
            Resets snap the counter to the lower bound at the start of each
            period. Period boundaries are UTC.
          </small>
        </div>

        <div className={styles.actions}>
          <button type="button" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className={styles.primary}
            disabled={!canSave}
            onClick={handleSubmit}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
